/**
 * Die vier Itemslots im HUD (Masterprompt Abschnitt 66).
 *
 * Bisher sah man seine Items nur im geoeffneten Shop - mitten im Gefecht
 * wusste man also nicht mehr, was man eigentlich dabeihat. Die Leiste zeigt
 * deshalb dauerhaft:
 *
 * - welcher Slot belegt ist (Kuerzel des Items),
 * - auf welcher Stufe es steht,
 * - ob es noch unterwegs ist.
 */

import { HERO_ITEM_SLOTS, getItem } from '@dueline/game-data';
import { getMaxStage } from '@dueline/game-rules';
import { t } from '../i18n/index.js';
import { useTap } from './usePress.js';
import type { MatchSnapshot } from '../net/MatchConnection.js';
import { gegenstandBild } from './faehigkeitBilder.js';

interface Props {
  snapshot: MatchSnapshot;
  onOpenShop: () => void;
}

export function ItemSlots({ snapshot, onOpenShop }: Props) {
  const slots = Array.from(
    { length: HERO_ITEM_SLOTS },
    (_, index) => snapshot.items[index] ?? null,
  );

  /*
   * Nicht onClick: Wer mit dem linken Daumen laeuft, bekommt vom Browser fuer
   * den zweiten Finger gar kein click (aufgefallen am 27.09.2026). Alle Slots
   * oeffnen denselben Shop, ein Haken genuegt also fuer alle.
   */
  const press = useTap(onOpenShop);

  return (
    <div className="dl-slots" role="group" aria-label={t.shop.slots}>
      {slots.map((item, index) => {
        if (!item) {
          return (
            <button
              key={`leer-${index}`}
              type="button"
              className="dl-slot dl-slot--empty"
              title={t.shop.emptySlot}
              {...press}
            >
              +
            </button>
          );
        }

        const definition = getItem(item.itemId);
        const unterwegs = item.deliverySecondsRemaining > 0;
        const bild = gegenstandBild(item.itemId);

        return (
          <button
            key={item.itemId}
            type="button"
            className={
              'dl-slot' + (unterwegs ? ' dl-slot--pending' : '') + (bild ? ' dl-slot--bild' : '')
            }
            title={`${definition?.name ?? item.itemId} · ${t.shop.upgrade} ${item.stage}/${getMaxStage(item.itemId)}`}
            {...press}
          >
            {/* Das Bild fuellt das Fach (18.10.2026), die Stufe wird ein
                Schild in der Ecke. Ohne Bild wie bisher das Kuerzel. */}
            {bild ? (
              <picture className="dl-slot__bild">
                <source type="image/avif" srcSet={bild.avif} sizes="64px" />
                <source type="image/webp" srcSet={bild.webp} sizes="64px" />
                <img src={bild.ersatz} alt="" decoding="async" />
              </picture>
            ) : (
              <span className="dl-slot__name">
                {(definition?.name ?? item.itemId).slice(0, 2).toUpperCase()}
              </span>
            )}
            <span className="dl-slot__stage">
              {unterwegs ? `${Math.ceil(item.deliverySecondsRemaining)}s` : item.stage}
            </span>
          </button>
        );
      })}
    </div>
  );
}
